/**
 * The device's copy of remote config, and the A/B tests in it (PRD §13 launch experiments).
 *
 * `rememberRemote` keeps the last GET /v1/config answer for a player and `restoreRemote` lays it over the
 * bundled defaults at the next start. A round has to be played with the rescue threshold and interstitial
 * rules the server resolves for that player, and an A/B arm changes them per player: a start whose config
 * request is still in flight (or that has no network) would otherwise play the defaults of another arm.
 * The server also accepts the shipped default threshold (apps/api rounds/config.ts), so a first launch with
 * no stored copy loses no round either.
 */
import type { RemoteConfig } from '../config/content.js';
import type { Analytics } from './analytics.js';
import type { KV } from './storage.js';

const KEY = 'remote.resolved';

interface Saved {
  /** the player the answer was resolved for: assignments are per player, never another account's */
  p: string;
  v: Partial<RemoteConfig>;
}

export function rememberRemote(kv: KV, playerId: string | null, resolved: Partial<RemoteConfig>): void {
  if (playerId) kv.set(KEY, JSON.stringify({ p: playerId, v: resolved } satisfies Saved));
}

/** Lay the stored answer for this player over `remote` (the bundled defaults). A copy that does not read is ignored: it is a cache. */
export function restoreRemote(kv: KV, playerId: string | null, remote: RemoteConfig): void {
  const raw = kv.get(KEY);
  if (!playerId || !raw) return;
  let saved: Partial<Saved>;
  try {
    saved = JSON.parse(raw) as Partial<Saved>;
  } catch {
    return;
  }
  if (saved.p !== playerId || !saved.v || typeof saved.v !== 'object') return;
  Object.assign(remote, saved.v, { flags: { ...remote.flags, ...(saved.v.flags ?? {}) } });
}

/**
 * The tests this player is in that really split players: at least two variants with weight. A test with
 * one arm taking everyone (the others parked at weight 0) is dormant, and says nothing about anyone.
 */
export function liveAssignments(remote: Pick<RemoteConfig, 'assignments' | 'experiments'>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, variant] of Object.entries(remote.assignments ?? {})) {
    const arms = remote.experiments[name]?.variants.filter((v) => v.weight > 0).length ?? 0;
    if (arms >= 2) out[name] = variant;
  }
  return out;
}

const exposed = new WeakMap<Analytics, Set<string>>();

/**
 * Once per session and test: the exposure event (`feature_action`, feature `experiments`) every metric of
 * the test is split by, so no new event name is needed (packages/schemas analytics.ts).
 */
export function trackExposures(analytics: Analytics, remote: Pick<RemoteConfig, 'assignments' | 'experiments'>): void {
  let seen = exposed.get(analytics);
  if (!seen) exposed.set(analytics, (seen = new Set()));
  for (const [experiment, variant] of Object.entries(liveAssignments(remote))) {
    if (seen.has(`${experiment}:${variant}`)) continue;
    seen.add(`${experiment}:${variant}`);
    analytics.track('feature_action', { feature: 'experiments', action: 'exposure', experiment, variant });
  }
}
